import type { Question } from '../../types';
import { HAB_MH_CONDITION } from './shared-options';

// ---------------------------------------------------------------------------
// Equipment Breakdown / Boiler & Machinery Supplement
// Source: Centered Partners MHMF intake — "Equipment Breakdown" sheet.
// HVAC, water heaters, boilers, generators, elevators, gates.
// ---------------------------------------------------------------------------

const EB_CONDITION = [
  { field: 'coverage_lines', operator: 'contains' as const, value: 'equipment_breakdown' },
  ...HAB_MH_CONDITION,
];

export const equipmentBreakdownQuestions: Question[] = [
  {
    id: 'eb_hvac',
    text: 'HVAC inventory by property',
    subtext: 'One entry per property with its HVAC unit counts and ages.',
    section: 'operations',
    group: 'equipment_breakdown',
    sort_order: 700,
    field_type: 'repeating_group',
    required: true,
    field_path: 'equipment.hvac_inventory',
    conditions: EB_CONDITION,
    repeating_group: {
      min_rows: 1,
      max_rows: 50,
      add_button_label: 'Add another property',
      row_label: 'Property HVAC',
      fields: [
        {
          id: 'eb_h_property',
          text: 'Property',
          section: 'operations',
          sort_order: 10,
          field_type: 'text',
          placeholder: 'Maple Court Apartments',
          required: true,
          field_path: 'property',
          conditions: [],
        },
        {
          id: 'eb_h_count',
          text: 'Unit count',
          section: 'operations',
          sort_order: 20,
          field_type: 'number',
          placeholder: '24',
          required: true,
          field_path: 'unit_count',
          conditions: [],
        },
        {
          id: 'eb_h_type',
          text: 'Type',
          section: 'operations',
          sort_order: 30,
          field_type: 'select',
          options: [
            { label: 'Rooftop units (RTU)', value: 'rtu' },
            { label: 'Split systems', value: 'split' },
            { label: 'Mini-splits', value: 'mini_split' },
            { label: 'Window units', value: 'window' },
            { label: 'Mixed', value: 'mixed' },
          ],
          required: true,
          field_path: 'type',
          conditions: [],
        },
        {
          id: 'eb_h_age',
          text: 'Average age (years)',
          section: 'operations',
          sort_order: 40,
          field_type: 'number',
          placeholder: '8',
          required: true,
          field_path: 'avg_age',
          conditions: [],
        },
        {
          id: 'eb_h_manufacturer',
          text: 'Primary manufacturer',
          section: 'operations',
          sort_order: 50,
          field_type: 'text',
          placeholder: 'Carrier',
          required: false,
          field_path: 'manufacturer',
          conditions: [],
        },
      ],
    },
    tags: ['equipment', 'lob', 'repeating'],
  },
  {
    id: 'eb_water_heaters',
    text: 'Describe the water heater inventory',
    subtext: 'Tank vs tankless, gas/electric, approximate ages.',
    section: 'operations',
    group: 'equipment_breakdown',
    sort_order: 710,
    field_type: 'textarea',
    placeholder: '120 tank units, gas, avg 6 yrs; clubhouse tankless, 2021.',
    required: true,
    field_path: 'equipment.water_heaters',
    conditions: EB_CONDITION,
    tags: ['equipment', 'lob'],
  },
  {
    id: 'eb_boilers',
    text: 'Are there boilers?',
    section: 'operations',
    group: 'equipment_breakdown',
    sort_order: 720,
    field_type: 'boolean',
    required: true,
    field_path: 'equipment.boilers',
    conditions: EB_CONDITION,
    tags: ['equipment', 'lob'],
  },
  {
    id: 'eb_boilers_detail',
    text: 'Boiler detail',
    subtext: 'Type, year, last inspection, and whether a jurisdictional certificate is on file.',
    section: 'operations',
    group: 'equipment_breakdown',
    sort_order: 730,
    field_type: 'textarea',
    required: true,
    field_path: 'equipment.boilers_detail',
    conditions: [...EB_CONDITION, { field: 'eb_boilers', operator: 'eq', value: true }],
    tags: ['equipment', 'lob'],
  },
  {
    id: 'eb_generators',
    text: 'Are there generators?',
    section: 'operations',
    group: 'equipment_breakdown',
    sort_order: 740,
    field_type: 'boolean',
    required: true,
    field_path: 'equipment.generators',
    conditions: EB_CONDITION,
    tags: ['equipment', 'lob'],
  },
  {
    id: 'eb_generators_detail',
    text: 'Generator detail',
    subtext: 'kW rating, year, fuel type, date of last load test.',
    section: 'operations',
    group: 'equipment_breakdown',
    sort_order: 750,
    field_type: 'textarea',
    required: true,
    field_path: 'equipment.generators_detail',
    conditions: [...EB_CONDITION, { field: 'eb_generators', operator: 'eq', value: true }],
    tags: ['equipment', 'lob'],
  },
  {
    id: 'eb_elevators',
    text: 'Are there elevators?',
    subtext: 'Mid/high-rise multifamily.',
    section: 'operations',
    group: 'equipment_breakdown',
    sort_order: 760,
    field_type: 'boolean',
    required: true,
    field_path: 'equipment.elevators',
    conditions: EB_CONDITION,
    tags: ['equipment', 'lob'],
  },
  {
    id: 'eb_elevators_detail',
    text: 'Elevator detail',
    subtext: 'Year, last state inspection date, maintenance contract vendor.',
    section: 'operations',
    group: 'equipment_breakdown',
    sort_order: 770,
    field_type: 'textarea',
    required: true,
    field_path: 'equipment.elevators_detail',
    conditions: [...EB_CONDITION, { field: 'eb_elevators', operator: 'eq', value: true }],
    tags: ['equipment', 'lob'],
  },
  {
    id: 'eb_gates',
    text: 'Are there access gates / entry systems?',
    section: 'operations',
    group: 'equipment_breakdown',
    sort_order: 780,
    field_type: 'boolean',
    required: true,
    field_path: 'equipment.gates',
    conditions: EB_CONDITION,
    tags: ['equipment', 'lob'],
  },
  {
    id: 'eb_gates_detail',
    text: 'Gate / entry system detail',
    subtext: 'Controller age, vendor, any prior failures.',
    section: 'operations',
    group: 'equipment_breakdown',
    sort_order: 790,
    field_type: 'textarea',
    required: false,
    field_path: 'equipment.gates_detail',
    conditions: [...EB_CONDITION, { field: 'eb_gates', operator: 'eq', value: true }],
    tags: ['equipment', 'lob'],
  },
  {
    id: 'eb_solar_ev',
    text: 'Any solar panels or EV charging stations installed?',
    section: 'operations',
    group: 'equipment_breakdown',
    sort_order: 800,
    field_type: 'boolean',
    required: true,
    field_path: 'equipment.solar_ev',
    conditions: EB_CONDITION,
    tags: ['equipment', 'lob'],
  },
  {
    id: 'eb_solar_ev_detail',
    text: 'Solar / EV charging detail',
    subtext: 'Capacity and year installed.',
    section: 'operations',
    group: 'equipment_breakdown',
    sort_order: 810,
    field_type: 'textarea',
    required: false,
    field_path: 'equipment.solar_ev_detail',
    conditions: [...EB_CONDITION, { field: 'eb_solar_ev', operator: 'eq', value: true }],
    tags: ['equipment', 'lob'],
  },
];
